import { IReservation, IReservationDayShift } from '@/interfaces/IReservation';
import { ITable } from '@/interfaces/ITable';
import {
  addDays,
  defaultBoardDate,
  formatTime,
  fromLocalDateTime,
  IReservationInstant,
} from './reservation-time';
import { isLate } from './reservation-status';

export interface IReservationTimelineBlock {
  reservation: IReservation;
  tableId: number;
  col: number;
  span: number;
  lane: number;
  linked: boolean;
  late: boolean;
}
export interface IReservationTimelineRow {
  table: ITable;
  blocks: IReservationTimelineBlock[];
  laneCount: number;
}
export interface IReservationTimelineLayout {
  startsAt: string;
  endsAt: string;
  columns: { startsAt: string; label: string; hour: boolean }[];
  rows: IReservationTimelineRow[];
  nowColumn: number | null;
}
export interface IReservationTimelineInput {
  date: string;
  timeZone: string;
  now: IReservationInstant;
  businessDayStartHour?: number;
  shifts: readonly IReservationDayShift[];
  tables: readonly ITable[];
  reservations: readonly IReservation[];
  sectors?: readonly { id: number; index: number }[];
  toleranceMin: number;
}

export function buildTimelineLayout(
  input: IReservationTimelineInput
): IReservationTimelineLayout {
  const step = 15 * 60000;
  const startsAt = input.shifts.length
    ? Math.min(
        ...input.shifts.map((shift) => new Date(shift.startsAt).getTime())
      ) -
      30 * 60000
    : fromLocalDateTime(input.date, '11:00', input.timeZone).getTime();
  const endsAt = input.shifts.length
    ? Math.max(
        ...input.shifts.map((shift) => new Date(shift.endsAt).getTime())
      ) +
      30 * 60000
    : fromLocalDateTime(
        addDays(input.date, 1),
        '01:00',
        input.timeZone
      ).getTime();
  const columns = Array.from(
    { length: Math.ceil((endsAt - startsAt) / step) },
    (_, index) => {
      const start = startsAt + index * step;
      const label = formatTime(start, input.timeZone);
      return {
        startsAt: new Date(start).toISOString(),
        label,
        hour: label.endsWith(':00'),
      };
    }
  );
  const sectorIndex = (table: ITable) =>
    input.sectors?.find((sector) => sector.id === table.sectorId)?.index ??
    Number.MAX_SAFE_INTEGER;
  const rows = input.tables
    .filter((table) => table.enabled && table.reservationsMode !== 'none')
    .slice()
    .sort(
      (a, b) =>
        sectorIndex(a) - sectorIndex(b) ||
        a.name.localeCompare(b.name, undefined, { numeric: true }) ||
        a.id - b.id
    )
    .map((table) => {
      const laneEnds: number[] = [];
      const blocks = input.reservations
        .filter((reservation) =>
          reservation.tables.some((value) => value.id === table.id)
        )
        .slice()
        .sort(
          (a, b) =>
            new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime() ||
            a.id - b.id
        )
        .flatMap((reservation) => {
          const start = Math.max(
            startsAt,
            new Date(reservation.startsAt).getTime()
          );
          const end = Math.min(endsAt, new Date(reservation.endsAt).getTime());
          if (end <= start) return [];
          const col = Math.floor((start - startsAt) / step);
          const span = Math.max(1, Math.ceil((end - start) / step));
          let lane = laneEnds.findIndex((laneEnd) => laneEnd <= col);
          if (lane === -1) lane = laneEnds.length;
          laneEnds[lane] = col + span;
          return [
            {
              reservation,
              tableId: table.id,
              col,
              span,
              lane,
              linked: reservation.tables.length > 1,
              late: isLate(reservation, input.now, input.toleranceMin),
            },
          ];
        });
      return { table, blocks, laneCount: Math.max(1, laneEnds.length) };
    });
  const now = new Date(input.now).getTime();
  const nowColumn =
    input.date ===
      defaultBoardDate(input.now, input.timeZone, input.businessDayStartHour) &&
    now >= startsAt &&
    now <= endsAt
      ? (now - startsAt) / step
      : null;
  return {
    startsAt: new Date(startsAt).toISOString(),
    endsAt: new Date(endsAt).toISOString(),
    columns,
    rows,
    nowColumn,
  };
}
